// src/services/OrderManager.js
import axiosInstance from "../../utils/axiosInstance"; // for admin
import userAxios from "../../utils/userAxios";         // for user

const BASE_URL = "/orders";

const OrderManager = {
  // 🟩 USER: Create a new book order
  async createOrder(userId, bookId) {
    const { data } = await userAxios.post(`${BASE_URL}/create`, null, {
      params: { userId, bookId },
    });
    return data.responseData;
  },

  // 🟩 USER: Create multiple book orders (Cart checkout)
  async createOrdersFromCart(userId, bookIds) {
    const { data } = await userAxios.post(`${BASE_URL}/create-multiple`, {
      userId,
      bookIds: Array.isArray(bookIds) ? bookIds : [],
    });
    return data.responseData;
  },

  // 🟩 USER: Get all orders placed by a user
  async getOrdersByUser(userId) {
    const { data } = await userAxios.get(`${BASE_URL}/user/${userId}`);
    return data.responseData;
  },

  // 🟥 ADMIN: Get all orders (paginated)
  async getAllOrders(pageable) {
    const { data } = await axiosInstance.get(`${BASE_URL}/all`, {
      params: pageable,
    });
    return data.responseData;
  },

  // 🟥 ADMIN: Get all pending orders
  async getPendingOrders() {
    const { data } = await axiosInstance.get(`${BASE_URL}/pending`);
    return data.responseData;
  },

  // 🟥 ADMIN: Get all cancelled orders
  async getCancelledOrders() {
    const { data } = await axiosInstance.get(`${BASE_URL}/cancelled`);
    return data.responseData;
  },

  // 🟥 ADMIN: Get all return orders
  async getReturnOrders() {
    const { data } = await axiosInstance.get(`${BASE_URL}/return-requests`);
    return data.responseData;
  },

  // 🟩 USER/ADMIN: Update order status
  async updateOrderStatus(orderId, status) {
    const { data } = await userAxios.put(`${BASE_URL}/${orderId}/status`, null, {
      params: { status },
    });
    return data.responseData;
  },

  // 🟩 USER/ADMIN: Update delivery status
  async updateDeliveryStatus(orderId, deliveryStatus) {
    const { data } = await userAxios.put(`${BASE_URL}/${orderId}/delivery-status`, null, {
      params: { deliveryStatus },
    });
    return data.responseData;
  },

  // 🟩 USER: Cancel an order
  async cancelOrder(orderId) {
    const { data } = await userAxios.delete(`${BASE_URL}/${orderId}/cancel`);
    return data.responseData;
  },

  // 🟩 USER: Return a book
  async returnBook(orderId, collectedBy) {
    const { data } = await userAxios.put(`${BASE_URL}/${orderId}/return`, null, {
      params: { collectedBy },
    });
    return data.responseData;
  },

  // 🟥 ADMIN: Schedule pickup for return
  async adminSchedulePickup(orderId) {
    const { data } = await axiosInstance.put(`${BASE_URL}/${orderId}/admin/schedule-pickup`);
    return data.responseData;
  },

  // 🟥 ADMIN: Mark return as in transit
  async adminMarkReturnInTransit(orderId) {
    const { data } = await axiosInstance.put(`${BASE_URL}/${orderId}/admin/mark-return-in-transit`);
    return data.responseData;
  },

  // 🟥 ADMIN: Mark book as returned
  async adminMarkBookReturned(orderId, collectedBy) {
    const { data } = await axiosInstance.put(
      `${BASE_URL}/${orderId}/admin/mark-book-returned`,
      null,
      { params: { collectedBy } }
    );
    return data.responseData;
  },

  // 🟥 ADMIN: Get books with users
  async getBooksWithUsers() {
    const { data } = await axiosInstance.get(`${BASE_URL}/books-with-users`);
    return data.responseData;
  },

  // 🟩 USER: Cancel order (specific user)
  async cancelUserOrder(userId, orderId) {
    const { data } = await userAxios.put(`${BASE_URL}/user/${userId}/order/${orderId}/cancel`);
    return data.responseData;
  },

  // 🟩 USER: Cancel return request
  async cancelUserReturnRequest(userId, orderId) {
    const { data } = await userAxios.put(
      `${BASE_URL}/user/${userId}/order/${orderId}/cancel-return`
    );
    return data.responseData;
  },

  // 🟥 ADMIN: Get most rented books
  async getMostRentedBooks(limit = 10) {
    const { data } = await axiosInstance.get(`/orders/most-rented`, {
      params: { limit },
    });
    return data;
  },

  // 🟥 ADMIN: Get order and user history for a specific book
  async getOrdersByBookId(bookId) {
    const { data } = await axiosInstance.get(`${BASE_URL}/book/${bookId}`);
    return data.responseData;
  },
};

export default OrderManager;
